import { getLessonsUtil } from '../../../main/utils/asset/lessons.util.ts';
import { getSentryUtil } from '../../../main/utils/monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { StepGraderType } from '../../constants/grading.ts';
import { mapWordToRefText } from '../../mappers/word-to-reftext.mapper.ts';
import { selectCurrentLessonTaskStep } from '../../selectors/current-lesson-task-step.selector.ts';
import { selectCurrentUserSentence } from '../../selectors/current-user-sentence.selector.ts';
import { selectCurrentUserStoryWord } from '../../selectors/current-user-story-word.selector.ts';

import { getLetterNamePronunciationGrader } from './letter-name-pronunciation.grader.ts';
import { getPhonemePronunciationGrader } from './phoneme-pronunciation.grader.util.ts';
import { getSentencePronunciationGrader } from './sentence-pronunciation.grader.ts';
import { getWordPronunciationGrader } from './word-pronunciation.grader.ts';

type PhonemePronunciationGraderType = ReturnType<typeof getPhonemePronunciationGrader>;
type WordPronunciationGraderType = ReturnType<typeof getWordPronunciationGrader>;
type LetterNamePronunciationGraderType = ReturnType<typeof getLetterNamePronunciationGrader>;
type SentencePronunciationGraderType = ReturnType<typeof getSentencePronunciationGrader>;

interface GraderTypeMap {
  [StepGraderType.PHONEME]: PhonemePronunciationGraderType;
  [StepGraderType.WORD]: WordPronunciationGraderType;
  [StepGraderType.LETTER_NAME]: LetterNamePronunciationGraderType;
  [StepGraderType.RHYME_SOUND]: WordPronunciationGraderType;
  [StepGraderType.STORY_WORD]: WordPronunciationGraderType;
  [StepGraderType.STORY_SENTENCE]: SentencePronunciationGraderType;
}

export class PronunciationGraderFactory {
  private static _instance: PronunciationGraderFactory;

  public static get instance(): PronunciationGraderFactory {
    if (!PronunciationGraderFactory._instance) {
      PronunciationGraderFactory._instance = new PronunciationGraderFactory();
    }
    return PronunciationGraderFactory._instance;
  }

  constructor(
    private readonly lessonsUtil = getLessonsUtil(),
    private readonly reduxStoreUtil = getReduxStoreUtil(),
    private readonly sentryUtil = getSentryUtil()
  ) {}

  public async createGrader<K extends StepGraderType>(options: { type: K }): Promise<GraderTypeMap[K]> {
    const { type } = options;

    try {
      // Create the grader instance
      const grader = this.getGraderInstance(type);

      // Get the appropriate reference text
      const refText = await this.getRefText(type);

      // Initialize the grader
      await grader.initialize(refText);

      return grader;
    } catch (error) {
      this.sentryUtil.trackError(error as Error, {
        context: 'GraderFactory',
        graderType: type,
      });
      throw error;
    }
  }

  private getGraderInstance<K extends StepGraderType>(type: K): GraderTypeMap[K] {
    switch (type) {
      case StepGraderType.PHONEME:
        return getPhonemePronunciationGrader() as GraderTypeMap[K];
      case StepGraderType.RHYME_SOUND:
      case StepGraderType.WORD:
      case StepGraderType.STORY_WORD:
        return getWordPronunciationGrader() as GraderTypeMap[K];
      case StepGraderType.LETTER_NAME:
        return getLetterNamePronunciationGrader() as GraderTypeMap[K];
      case StepGraderType.STORY_SENTENCE:
        return getSentencePronunciationGrader() as GraderTypeMap[K];
      default:
        throw new Error(`Unsupported grader type: ${type as unknown as string}`);
    }
  }

  private async getRefText(type: StepGraderType): Promise<string> {
    switch (type) {
      case StepGraderType.PHONEME:
        return await this.getLetterSoundRefText();
      case StepGraderType.WORD:
        return await this.getWordRefText();
      case StepGraderType.LETTER_NAME:
        return await this.getLetterNameRefText();
      case StepGraderType.RHYME_SOUND:
        return await this.getRhymingSoundRefText();
      case StepGraderType.STORY_WORD:
        return await this.getStoryWordRefText();
      case StepGraderType.STORY_SENTENCE:
        return await this.getStorySentenceRefText();
      default:
        throw new Error(`Unsupported grader type: ${type as unknown as string}`);
    }
  }

  private async getLetterSoundRefText(): Promise<string> {
    const state = await this.reduxStoreUtil.getRootState();
    const { task, step } = selectCurrentLessonTaskStep(state);
    const refText = this.lessonsUtil.getStepLetterRefText(task, step);
    if (!refText) {
      throw new Error('Invalid step - letter ref text not found');
    }
    return refText;
  }

  private async getWordRefText(): Promise<string> {
    const state = await this.reduxStoreUtil.getRootState();
    const { task, step } = selectCurrentLessonTaskStep(state);
    const word = this.lessonsUtil.getStepWord(task, step);
    if (!word) {
      throw new Error('Invalid step - word reference text not found');
    }
    return mapWordToRefText(word);
  }

  private async getLetterNameRefText(): Promise<string> {
    const state = await this.reduxStoreUtil.getRootState();
    const { task, step } = selectCurrentLessonTaskStep(state);
    const refText = this.lessonsUtil.getStepLetterNameRefText(task, step);
    if (!refText) {
      throw new Error('Invalid step - letter name ref text not found');
    }
    return refText;
  }

  private async getRhymingSoundRefText(): Promise<string> {
    const state = await this.reduxStoreUtil.getRootState();
    const { task, step } = selectCurrentLessonTaskStep(state);
    const refText = this.lessonsUtil.getStepRhymingSoundRefText(task, step);
    if (!refText) {
      throw new Error('Invalid step - rhyming sound ref text not found');
    }
    return refText;
  }

  private async getStoryWordRefText(): Promise<string> {
    const state = await this.reduxStoreUtil.getRootState();
    const word = selectCurrentUserStoryWord(state);
    if (!word) {
      throw new Error('Story word not found');
    }
    return word;
  }

  private async getStorySentenceRefText(): Promise<string> {
    const state = await this.reduxStoreUtil.getRootState();
    const sentence = selectCurrentUserSentence(state);
    if (!sentence) {
      throw new Error('Story sentence not found');
    }
    return sentence;
  }
}

export const getPronunciationGraderFactory = (): PronunciationGraderFactory => PronunciationGraderFactory.instance;
